import { alpha, createTheme } from "@mui/material/styles";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import React from "react";
import { tokens } from "./tokens";

export { tokens, gradientBorder, glassSurface } from "./tokens";

const fontFamily =
  "'Libre Franklin', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

/**
 * Touch screens: enlarge the hit area to 44×44 around small controls without
 * changing their visual size (Apple HIG minimum target).
 */
export const touchHitArea = {
  "@media (pointer: coarse)": {
    "&::after": {
      content: '""',
      position: "absolute",
      left: "50%",
      top: "50%",
      width: "max(100%, 44px)",
      height: "max(100%, 44px)",
      transform: "translate(-50%, -50%)",
    },
  },
};

const chromeGlass = {
  backgroundColor: tokens.overlay,
  backgroundImage: "none",
  backdropFilter: "blur(24px) saturate(150%)",
  WebkitBackdropFilter: "blur(24px) saturate(150%)",
  border: `1px solid ${tokens.borderStrong}`,
  boxShadow: `${tokens.glass.highlight}, 0 24px 60px -16px rgba(0,0,0,0.85)`,
};

export const theme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: tokens.green,
      light: tokens.greenLight,
      dark: tokens.greenDark,
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: tokens.gold,
      light: tokens.goldLight,
      dark: tokens.goldDark,
      contrastText: "#111111",
    },
    background: {
      default: tokens.canvas,
      paper: tokens.card,
    },
    text: {
      primary: tokens.textPrimary,
      secondary: tokens.textSecondary,
      disabled: tokens.textMuted,
    },
    divider: tokens.border,
    success: { main: tokens.positive },
    error: { main: tokens.negative },
    warning: { main: tokens.warning },
    info: { main: tokens.info },
    action: {
      hover: tokens.hover,
      selected: "rgba(255,255,255,0.10)",
    },
    cards: {
      main: "rgba(255,255,255,0.04)",
      secondary: "rgba(255,255,255,0.04)",
      additional: tokens.raised,
    },
    surface: {
      canvas: tokens.canvas,
      sidebar: tokens.sidebar,
      card: tokens.card,
      raised: tokens.raised,
      overlay: tokens.overlay,
    },
    chart: { ...tokens.chart },
  },
  shape: { borderRadius: 8 },
  typography: {
    fontFamily,
    h1: { fontSize: "2.25rem", fontWeight: 700, letterSpacing: "-0.025em", lineHeight: 1.15 },
    h2: { fontSize: "1.875rem", fontWeight: 700, letterSpacing: "-0.025em", lineHeight: 1.2 },
    h3: { fontSize: "1.5rem", fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.25 },
    h4: { fontSize: "1.5rem", fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.25 },
    h5: { fontSize: "1.25rem", fontWeight: 700, letterSpacing: "-0.015em", lineHeight: 1.3 },
    h6: { fontSize: "1.0625rem", fontWeight: 650, letterSpacing: "-0.01em", lineHeight: 1.35 },
    subtitle1: { fontSize: "0.9375rem", fontWeight: 600, lineHeight: 1.45 },
    subtitle2: { fontSize: "0.875rem", fontWeight: 600, lineHeight: 1.45 },
    body1: { fontSize: "0.9375rem", lineHeight: 1.55 },
    body2: { fontSize: "0.875rem", lineHeight: 1.5 },
    caption: { fontSize: "0.75rem", lineHeight: 1.45 },
    // Glass direction: quiet sentence-case labels instead of tracked caps.
    overline: {
      fontSize: "0.75rem",
      fontWeight: 600,
      letterSpacing: "0.01em",
      lineHeight: 1.45,
      textTransform: "none",
    },
    button: { fontWeight: 650, textTransform: "none", letterSpacing: 0 },
  },
  breakpoints: {
    values: { xs: 0, sm: 600, md: 900, lg: 1200, xl: 1536 },
  },
  components: {
    // Resolve media queries on first render (no desktop flash on phones).
    MuiUseMediaQuery: { defaultProps: { noSsr: true } },
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          WebkitFontSmoothing: "antialiased",
          MozOsxFontSmoothing: "grayscale",
          WebkitTextSizeAdjust: "100%",
        },
        body: {
          backgroundColor: tokens.canvas,
          fontVariantNumeric: "tabular-nums",
          // Keep content clear of the notch in landscape (viewport-fit=cover).
          paddingLeft: "env(safe-area-inset-left)",
          paddingRight: "env(safe-area-inset-right)",
        },
        // Native controls don't inherit the page font by default (ButtonBase renders <button>).
        "button, input, select, textarea": { fontFamily: "inherit" },
        "::selection": {
          backgroundColor: alpha(tokens.gold, 0.35),
          color: tokens.textPrimary,
        },
        "*::-webkit-scrollbar": { width: 8, height: 8 },
        "*::-webkit-scrollbar-track": { background: "transparent" },
        "*::-webkit-scrollbar-thumb": {
          backgroundColor: "rgba(255,255,255,0.14)",
          borderRadius: 8,
          border: "2px solid transparent",
          backgroundClip: "padding-box",
        },
        "*::-webkit-scrollbar-thumb:hover": {
          backgroundColor: "rgba(255,255,255,0.24)",
        },
        "@media (prefers-reduced-motion: reduce)": {
          "*, *::before, *::after": {
            animationDuration: "0.01ms !important",
            animationIterationCount: "1 !important",
            transitionDuration: "0.01ms !important",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          backgroundColor: "rgba(255,255,255,0.035)",
          border: `1px solid ${tokens.border}`,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: { borderRadius: tokens.radius.lg },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.pill,
          fontWeight: 650,
          paddingInline: 18,
          minHeight: 38,
          "@media (pointer: coarse)": { minHeight: 44 },
          transition: "background 160ms ease, box-shadow 160ms ease, transform 120ms ease",
          "&:active": { transform: "translateY(1px)" },
        },
        sizeSmall: { minHeight: 32, paddingInline: 14, fontSize: "0.8125rem" },
        sizeLarge: { minHeight: 48, paddingInline: 24, fontSize: "0.9375rem" },
        contained: {
          "&.MuiButton-colorPrimary, &.MuiButton-colorSecondary": {
            background: tokens.goldGradient,
            color: "#111111",
            boxShadow: tokens.goldGlow,
            "&:hover": {
              background: "linear-gradient(180deg, #EECAA2 0%, #D3A273 100%)",
              boxShadow: "0 12px 34px -10px rgba(205,160,117,0.9)",
            },
          },
          "&.Mui-disabled": {
            background: "rgba(255,255,255,0.08)",
            color: "rgba(255,255,255,0.35)",
            boxShadow: "none",
          },
        },
        outlined: {
          borderColor: tokens.borderStrong,
          color: tokens.textPrimary,
          backgroundColor: "rgba(255,255,255,0.03)",
          backdropFilter: "blur(12px)",
          "&:hover": { borderColor: "rgba(255,255,255,0.32)", backgroundColor: "rgba(255,255,255,0.07)" },
          "&.MuiButton-colorError": {
            borderColor: alpha(tokens.negative, 0.45),
            color: tokens.negative,
            "&:hover": { borderColor: tokens.negative, backgroundColor: alpha(tokens.negative, 0.1) },
          },
        },
        text: {
          "&.MuiButton-colorPrimary": { color: tokens.goldLight },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: { borderRadius: "50%", ...touchHitArea },
      },
    },
    MuiButtonGroup: {
      styleOverrides: {
        root: { borderRadius: tokens.radius.pill },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(0,0,0,0.35)",
          border: `1px solid ${tokens.border}`,
          borderRadius: tokens.radius.pill,
          padding: 3,
          gap: 2,
        },
        grouped: {
          border: 0,
          borderRadius: `${tokens.radius.pill}px !important`,
          margin: 0,
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          lineHeight: 1.2,
          color: tokens.textSecondary,
          fontSize: "0.75rem",
          minHeight: 28,
          padding: "5px 12px",
          "@media (pointer: coarse)": { minHeight: 32 },
          ...touchHitArea,
          "&:hover": { backgroundColor: tokens.hover, color: tokens.textPrimary },
          // In-card controls (time range, granularity): gold, so they read as
          // secondary to the white-glass page tabs.
          "&.Mui-selected": {
            color: tokens.goldLight,
            backgroundColor: alpha(tokens.gold, 0.14),
            boxShadow: `inset 0 0 0 1px ${alpha(tokens.gold, 0.32)}`,
            "&:hover": { backgroundColor: alpha(tokens.gold, 0.2) },
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 40 },
        indicator: { backgroundColor: tokens.gold, height: 2, borderRadius: 2 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 40,
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.875rem",
          letterSpacing: 0,
          color: tokens.textSecondary,
          // textColor="inherit" tabs get opacity .6 from MUI; keep contrast via color instead.
          opacity: 1,
          "&:not(.Mui-selected)": { opacity: 1, color: "rgba(255,255,255,0.72)" },
          "&.Mui-selected": { color: tokens.textPrimary },
          ...touchHitArea,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: tokens.radius.pill, fontWeight: 600 },
        clickable: touchHitArea,
        sizeSmall: { height: 22, fontSize: "0.6875rem" },
        outlined: { borderColor: tokens.borderStrong },
      },
    },
    MuiTooltip: {
      defaultProps: { arrow: false, enterTouchDelay: 0, leaveTouchDelay: 4000 },
      styleOverrides: {
        tooltip: {
          ...chromeGlass,
          color: tokens.textPrimary,
          borderRadius: tokens.radius.sm,
          fontSize: "0.75rem",
          fontWeight: 500,
          padding: "8px 11px",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        // Phones: every dialog is a bottom sheet (one pattern, thumb-reachable, safe areas respected).
        container: { "@media (max-width: 599.95px)": { alignItems: "flex-end" } },
        paper: {
          ...chromeGlass,
          backgroundColor: "rgba(18,18,20,0.9)",
          borderRadius: tokens.radius.xl - 4,
          boxShadow: `${tokens.glass.highlight}, 0 40px 100px -20px rgba(0,0,0,0.9)`,
          "@media (max-width: 599.95px)": {
            margin: 0,
            width: "100%",
            maxWidth: "100%",
            borderRadius: "28px 28px 0 0",
            borderBottom: 0,
            maxHeight: "calc(100dvh - env(safe-area-inset-top) - 12px)",
            paddingBottom: "env(safe-area-inset-bottom)",
          },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: { fontSize: "1.125rem", fontWeight: 700, letterSpacing: "-0.01em", padding: "22px 24px 12px" },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: { padding: "14px 24px 20px", gap: 8, "& > :not(style) ~ :not(style)": { marginLeft: 0 } },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(0,0,0,0.62)",
          backdropFilter: "blur(6px)",
          "&.MuiBackdrop-invisible": { backgroundColor: "transparent", backdropFilter: "none" },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          ...chromeGlass,
          backgroundColor: "rgba(14,14,16,0.86)",
          border: "none",
          borderLeft: `1px solid ${tokens.border}`,
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: { ...chromeGlass, borderRadius: tokens.radius.md },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: { ...chromeGlass, borderRadius: tokens.radius.md },
        list: { padding: 6 },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          fontSize: "0.875rem",
          minHeight: 38,
          "@media (pointer: coarse)": { minHeight: 44 },
          "&.Mui-selected": { backgroundColor: "rgba(255,255,255,0.10)" },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.sm,
          "&.Mui-selected": {
            backgroundColor: "rgba(255,255,255,0.10)",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.14)" },
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: tokens.border },
      },
    },
    // Glass fields: label above the field (no notched border), 14px radius, gold focus.
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          backgroundColor: "rgba(255,255,255,0.04)",
          "& .MuiOutlinedInput-notchedOutline": { borderColor: tokens.border, top: 0 },
          "& .MuiOutlinedInput-notchedOutline legend": { display: "none" },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: tokens.borderStrong },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: alpha(tokens.gold, 0.7),
            borderWidth: 1,
          },
          "&.Mui-disabled": { backgroundColor: "rgba(255,255,255,0.02)" },
          "&.Mui-disabled .MuiOutlinedInput-notchedOutline": { borderColor: tokens.border },
          // One height for every single-line field (small or medium): 48px.
          "&:not(.MuiInputBase-multiline)": { minHeight: 48 },
          "&:not(.MuiInputBase-multiline) .MuiOutlinedInput-input": { paddingTop: 12, paddingBottom: 12 },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        // 16px on touch screens: iOS Safari zooms into anything smaller on focus.
        input: { "@media (pointer: coarse)": { fontSize: 16 } },
      },
    },
    MuiInputLabel: {
      defaultProps: { shrink: true },
      styleOverrides: {
        root: {
          position: "relative",
          transform: "none",
          maxWidth: "100%",
          marginBottom: 6,
          fontSize: "0.75rem",
          fontWeight: 500,
          lineHeight: 1.4,
          color: tokens.textSecondary,
          pointerEvents: "auto",
          "&.Mui-focused": { color: tokens.goldLight },
          "& .MuiFormLabel-asterisk": { display: "none" },
        },
      },
    },
    MuiSelect: {
      defaultProps: { IconComponent: ExpandMoreRoundedIcon },
      styleOverrides: {
        icon: { fontSize: 20, right: 10, color: tokens.textSecondary },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: { marginLeft: 2, marginRight: 2, marginTop: 6, color: tokens.textMuted },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        icon: React.createElement("span", { className: "teks-check" }),
        checkedIcon: React.createElement(
          "span",
          { className: "teks-check teks-check--on" },
          React.createElement(CheckRoundedIcon, { sx: { fontSize: 14 } })
        ),
      },
      styleOverrides: {
        root: {
          ...touchHitArea,
          "& .teks-check": {
            width: 18,
            height: 18,
            borderRadius: 5,
            display: "grid",
            placeItems: "center",
            border: `1.5px solid ${alpha("#FFFFFF", 0.3)}`,
            backgroundColor: "rgba(255,255,255,0.03)",
            transition: "background-color 140ms ease, border-color 140ms ease",
          },
          "& .teks-check--on": { borderColor: tokens.gold, background: tokens.goldGradient, color: "#111" },
          "&:hover .teks-check": { borderColor: alpha(tokens.gold, 0.6) },
          "&.Mui-focusVisible .teks-check": { outline: `2px solid ${alpha(tokens.gold, 0.7)}`, outlineOffset: 2 },
          "&.Mui-disabled .teks-check": { opacity: 0.4 },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: { "&.Mui-checked": { color: tokens.gold } },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottom: `1px solid ${tokens.borderSubtle}` },
        head: {
          fontSize: "0.75rem",
          fontWeight: 600,
          color: tokens.textSecondary,
          backgroundColor: "rgba(18,18,20,0.96)",
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: { color: tokens.textSecondary, borderBottom: "none" },
        selectLabel: { fontSize: "0.8125rem" },
        displayedRows: { fontSize: "0.8125rem" },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: tokens.radius.pill, backgroundColor: "rgba(255,255,255,0.08)" },
        bar: { borderRadius: tokens.radius.pill },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: tokens.radius.md, alignItems: "center", backdropFilter: "blur(12px)" },
      },
    },
    MuiSnackbarContent: {
      styleOverrides: {
        root: { ...chromeGlass, color: tokens.textPrimary, borderRadius: tokens.radius.md },
      },
    },
    MuiLink: {
      defaultProps: { underline: "hover" },
      styleOverrides: {
        root: { color: tokens.textPrimary, fontWeight: 600 },
      },
    },
    // On: gold track + white thumb. Off: dim track + dim thumb (never brighter than "on").
    MuiSwitch: {
      styleOverrides: {
        root: { "@media (pointer: coarse)": { overflow: "visible" } },
        switchBase: {
          ...touchHitArea,
          color: "rgba(255,255,255,0.7)",
          "&.Mui-checked": { color: "#FFFFFF" },
          "&.Mui-checked + .MuiSwitch-track": { backgroundColor: tokens.gold, opacity: 1 },
        },
        thumb: { boxShadow: "0 1px 3px rgba(0,0,0,0.5)" },
        track: { backgroundColor: "rgba(255,255,255,0.16)", opacity: 1 },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: { fontWeight: 700 },
      },
    },
  },
});
